<template>
    <div class="relative block w-full">
        <button
            ref="triggerRef"
            class="text-slate-800 rounded w-full md:w-auto md:justify-start justify-center items-center gap-2 flex px-2 py-1.5 hover:bg-slate-100 transition-colors duration-200 disabled:opacity-50 disabled:bg-transparent"
            @click.stop="isOpen = !isOpen"
            v-click-outside="() => (isOpen = false)"
        >
            <LoungeIcon icon="advanced_search" />
            <span class="text-sm font-medium">Advanced Options</span>
            <div
                v-if="selectedItems.length > 0"
                class="px-1.5 py-0.5 bg-slate-800 rounded-[999px] flex-col justify-center items-center gap-2.5 flex text-white text-xs font-medium leading-none w-4"
            >
                {{ selectedItems.length }}
            </div>
            <Icon
                icon="chevron-down"
                aria-hidden="true"
                width="12"
                height="12"
                :class="{ 'rotate-180': isOpen }"
                class="transition-transform"
            />
        </button>

        <div
            v-show="isOpen"
            class="absolute z-20 left-0 right-0 top-full pt-2 bg-white rounded-lg shadow-lg p-4 flex flex-col gap-4 min-w-max"
        >
            <label
                for="membership"
                class="gap-2 flex flex-col text-slate-600 text-sm font-medium"
            >
                Membership access
                <UiAutosuggestFlexible
                    :preloadData="true"
                    :preloadedOptions="loungeMemberships"
                    mode="multiple"
                    label="Membership access"
                    placeholder="Membership"
                    :showCounter="true"
                    :selectedOptions="userCredentials.memberships"
                    valueKey="code"
                    labelKey="name"
                    displayKey="name"
                    @toggleSelect="toggleSelectMembership"
                    id="membership"
                    name="membership"
                    buttonClass="w-full py-2 px-3 bg-white rounded shadow border border-slate-200 justify-between w-full items-center flex text-slate-500 text-sm font-normal  whitespace-nowrap focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300 "
                />
            </label>
            <label
                for="ffp"
                class="gap-2 flex flex-col text-slate-600 text-sm font-medium w-full"
            >
                Frequent flyer access
                <UiAutosuggestFlexible
                    :preloadData="true"
                    mode="multiple"
                    label="Frequent flyer access"
                    placeholder="Airline or level"
                    :showCounter="true"
                    :selectedOptions="userCredentials.elite_status"
                    valueKey="status_code"
                    labelKey="name"
                    displayKey="name"
                    id="ffp"
                    @filter="(v) => (ffpFilterInput = v)"
                    buttonClass="py-2 px-3 bg-white rounded shadow border border-slate-200 justify-between w-full items-center flex text-slate-500 text-sm font-normal  whitespace-nowrap focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300"
                >
                    <template #options>
                        <li
                            v-for="(airline, index) in filteredFfp"
                            :key="`header-${index}`"
                            class="w-full mb-2 list-none"
                        >
                            <h3
                                class="rounded justify-start items-center p-2 flex grow shrink basis-0 text-slate-800 text-sm font-medium leading-tight"
                            >
                                {{ airline.full_name }}
                            </h3>
                            <div
                                v-for="(
                                    ffp, i
                                ) in airline.frequent_flyer_programs"
                                :key="`ffp-${i}`"
                                class="p-2 rounded justify-start items-center flex gap-2 text-slate-800 text-sm font-normal leading-tight whitespace-nowrap"
                            >
                                <input
                                    type="checkbox"
                                    class="py-0.5 justify-start items-center flex text-orange-600 w-4 h-4 bg-white rounded border border-slate-300 focus:ring-0 focus:ouline-none"
                                    :id="ffp.status_code"
                                    :name="ffp.status_name"
                                    @change="toggleSelectFfp(ffp)"
                                    :checked="isSelected(ffp)"
                                />
                                <label :for="ffp.status_code">
                                    <p>{{ ffp.status_name }}</p>
                                </label>
                            </div>
                        </li>
                        <p
                            v-if="!filteredFfp.length"
                            class="p-3 bg-white rounded-lg text-slate-800 text-sm leading-tight"
                        >
                            No results found, try adjusting your search
                        </p>
                    </template>
                </UiAutosuggestFlexible>
            </label>
            <label
                for="card"
                class="gap-2 flex flex-col text-slate-600 text-sm font-medium w-full"
            >
                Credit card access
                <UiAutosuggestFlexible
                    :preloadData="false"
                    mode="multiple"
                    label="Credit card access"
                    placeholder="Credit card"
                    :showCounter="true"
                    :options="creditCards"
                    :selectedOptions="userCredentials.cards"
                    valueKey="id"
                    id="card"
                    labelKey="title"
                    displayKey="title"
                    url="/cards/search/"
                    @toggleSelect="toggleSelectCard"
                    buttonClass="py-2 px-3 bg-white rounded shadow border border-slate-200 justify-between w-full items-center flex text-slate-500 text-sm font-normal  whitespace-nowrap focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300 "
                />
            </label>
            <label
                for="special_circumstances"
                class="gap-2 flex flex-col text-slate-600 text-sm font-medium w-full"
            >
                Special circumstances
                <UiMultiSelect
                    label="Special circumstances"
                    :showCounter="true"
                    :options="specialCircumstances"
                    v-model:modelValue="userCredentials.special_circumstances"
                    labelKey="name"
                    valueKey="code"
                    :disabled="!specialCircumstances.length"
                    id="special_circumstances"
                    buttonClass="py-2 px-3 bg-white rounded shadow border border-slate-200 justify-between w-full items-center flex text-slate-500 text-sm font-normal  whitespace-nowrap focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300 "
                />
            </label>
        </div>
    </div>
</template>

<script setup>
import { useLoungesStore } from '@/store/lounges';
import { storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import vClickOutside from 'click-outside-vue3';
import Icon from '@/components/ui/icon/icon.vue';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import UiAutosuggestFlexible from '@/components/ui/inputs/autosuggest-flexible/ui-autosuggest-flexible.vue';
import UiMultiSelect from '@/components/ui/inputs/multi-select/ui-multi-select.vue';

const loungesStore = useLoungesStore();
const {
    creditCards,
    loungeMemberships,
    statusesByAirline,
    specialCircumstances,
    userCredentials,
} = storeToRefs(loungesStore);

const isOpen = ref(false);
const triggerRef = ref(null);
const ffpFilterInput = ref('');

const selectedItems = computed(() => {
    return [
        ...userCredentials.value.memberships,
        ...userCredentials.value.cards,
        ...userCredentials.value.special_circumstances,
        ...userCredentials.value.elite_status,
    ];
});

const filteredFfp = computed(() => {
    console.log(
        statusesByAirline.value.filter((airline) => {
            return (
                airline.full_name
                    .toLowerCase()
                    .includes(ffpFilterInput.value.toLowerCase()) ||
                airline.frequent_flyer_programs.some((ffp) =>
                    ffp.status_name
                        .toLowerCase()
                        .includes(ffpFilterInput.value.toLowerCase())
                )
            );
        })
    );
    return statusesByAirline.value.filter((airline) => {
        return (
            airline.full_name
                .toLowerCase()
                .includes(ffpFilterInput.value.toLowerCase()) ||
            airline.frequent_flyer_programs.some((ffp) =>
                ffp.status_name
                    .toLowerCase()
                    .includes(ffpFilterInput.value.toLowerCase())
            )
        );
    });
});

const toggleSelectMembership = (membership) => {
    const index = userCredentials.value.memberships.findIndex(
        (m) => m.code === membership.code
    );
    if (index === -1) {
        userCredentials.value.memberships.push(membership);
    } else {
        userCredentials.value.memberships.splice(index, 1);
    }
};

const toggleSelectCard = (card) => {
    const index = userCredentials.value.cards.findIndex(
        (c) => c.id === card.id
    );
    if (index === -1) {
        userCredentials.value.cards.push(card);
    } else {
        userCredentials.value.cards.splice(index, 1);
    }
};

const toggleSelectFfp = (ffp) => {
    const index = userCredentials.value.elite_status.findIndex(
        (s) => s.status_code === ffp.status_code
    );
    if (index === -1) {
        userCredentials.value.elite_status.push(ffp);
    } else {
        userCredentials.value.elite_status.splice(index, 1);
    }
};

const isSelected = (ffp) => {
    return userCredentials.value.elite_status.some(
        (s) => s.status_code === ffp.status_code
    );
};
</script>
